import React, { useEffect } from 'react';

import { toNumber } from 'lodash-es';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorPage from '@/components/errors/ErrorPage';
import InternalServerError from '@/components/errors/InternalServerError';
import SingleColumnTemplate from '@/components/layouts/SingleColumnTemplate';
import UiStackTemplate from '@/components/layouts/UiStackTemplate';
import PageTitle from '@/components/ui/headers/PageTitle';
import useEditableChecker from '@/hooks/absences/useEditableChecker';
import useTimeExceededChecker from '@/hooks/absences/useTimeExceededChecker';
import useFetch from '@/hooks/common/useFetch';
import useModal from '@/hooks/common/useModal';

import styles from './AbsencesDetailPage.module.scss';
import AbsencesDetailBody from './detail/AbsencesDetailBody';
import AbsencesDetailFooter from './detail/AbsencesDetailFooter';
import AbsencesEmpty from './parts/AbsencesEmpty';
import AbsencesRestricted from './parts/AbsencesRestricted';
import { type ContactType, type Reason } from './types';

interface Absence {
  id: number;
  date: string;
  organization_membership_id: string;
}

type AbsenceLogStatus = 'created' | 'updated' | 'canceled';

interface AbsenceLog {
  id: number;
  status: AbsenceLogStatus;
  contact_type: ContactType;
  reason: Reason;
  detail_comment: string;
  go_to_school_at: string | null;
  leave_school_at: string | null;
  disease_name: string | null;
  symptom: string | null;
  body_temperature: string | null;
  doctors_instruction: boolean | null;
  relationship: string | null;
  transportation: string | null;
  is_created_teacher: boolean;
  confirmation?: {
    confirmed: boolean;
  };
}

export interface OrganizationManagement {
  allowed_absence: boolean;
  absence_time_limit: string | null;
}

export interface AbsenceDetailPayload {
  absence: Absence;
  absence_log: AbsenceLog;
  organization_management?: OrganizationManagement;
}

type DetailState =
  'ok' | 'notAllowed' | 'forbidden' | 'notFound' | 'serverError' | null;

const AbsencesDetailPage: React.FC = () => {
  const [detailState, setDetailState] = React.useState<DetailState>(null);
  const navigate = useNavigate();
  const { absenceId = '', contactType = '' } = useParams();
  const { showModal } = useModal();
  const { isTimeExceededWithLimit } = useTimeExceededChecker();
  const { isEditable } = useEditableChecker();

  const {
    fetch: fetchAbsence,
    isFetching,
    status,
    data: payload,
  } = useFetch<AbsenceDetailPayload>(
    'GET',
    `/api/school_communication/absences/${absenceId}/${contactType}`,
  );

  const handleOnclickBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(
      `/absences/member/${payload?.absence.organization_membership_id || ''}`,
    );
  };

  useEffect(() => {
    setDetailState(null);
    void fetchAbsence();
  }, [fetchAbsence]);

  const handleOnRightLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (
      isTimeExceededWithLimit(
        payload?.absence.date,
        payload?.organization_management?.absence_time_limit,
      )
    ) {
      showModal('AbsencesTimeExceededModal', {
        timeLimit: payload?.organization_management?.absence_time_limit,
        postDate: payload?.absence.date,
      });
    } else {
      navigate(`/absences/${absenceId}/type/${contactType}/edit`);
    }
  };

  React.useEffect(() => {
    setDetailState(null);
    const statusCode = toNumber(status);
    if (!isFetching) {
      if (statusCode === 200) {
        return setDetailState('ok');
      }

      if (statusCode === 423) {
        return setDetailState('notAllowed');
      }

      if (statusCode === 403) {
        return setDetailState('forbidden');
      }

      if (statusCode >= 400 && statusCode < 500) {
        return setDetailState('notFound');
      }

      if (statusCode >= 500 && statusCode < 600) {
        return setDetailState('serverError');
      }

      return setDetailState(null);
    }
  }, [status, isFetching]);

  const renderSwitch: (
    state: DetailState,
    payload: AbsenceDetailPayload | null,
  ) => React.ReactNode = (state: DetailState) => {
    if (state === 'ok') {
      if (payload) {
        return <AbsencesDetailBody payload={payload} />;
      }

      return <AbsencesEmpty />;
    }

    if (state === 'serverError') {
      return <InternalServerError />;
    }

    if (state === 'notAllowed') {
      return <AbsencesRestricted type="notAllowed" />;
    }

    return null;
  };

  return detailState === 'notFound' ? (
    <ErrorPage type="not_found" />
  ) : detailState === 'forbidden' ? (
    <ErrorPage type="forbidden" />
  ) : (
    <SingleColumnTemplate>
      <div className={styles.wrapper}>
        {detailState === 'ok' && !isFetching ? (
          <PageTitle
            title="欠席連絡詳細"
            backLinkLabel="欠席連絡一覧"
            onBackLinkClick={handleOnclickBack}
            rightLinkLabel={isEditable(payload?.absence.date) ? '編集' : null}
            onRightLinkClick={handleOnRightLinkClick}
          />
        ) : detailState === 'serverError' ? null : (
          <PageTitle
            title="欠席連絡詳細"
            backLinkLabel="欠席連絡一覧"
            onBackLinkClick={handleOnclickBack}
          />
        )}
        <UiStackTemplate isLoading={isFetching}>
          <div className={styles.inner}>
            {renderSwitch(detailState, payload)}
          </div>
        </UiStackTemplate>
        {detailState === 'ok' && !isFetching && (
          <AbsencesDetailFooter
            date={payload?.absence.date}
            onBack={handleOnclickBack}
            organizationMembershipId={
              payload?.absence.organization_membership_id
            }
            organizationManagement={payload?.organization_management}
          />
        )}
      </div>
    </SingleColumnTemplate>
  );
};

export default AbsencesDetailPage;
